import {
  BillingPlan,
  OrganizationBillingInterval,
  OrganizationBillingPlan,
  RecurringInterval,
} from "@workspace/billing";

const CHECKOUT_INTENT_STORAGE_KEY = "pricing-checkout-intent";

const CHECKOUT_PLAN_KEY = "checkoutPlan";
const CHECKOUT_INTERVAL_KEY = "checkoutInterval";
const CHECKOUT_SOURCE_KEY = "checkoutSource";
const CHECKOUT_SOURCE_PRICING = "pricing";

const CHECKOUT_PLANS = new Set([
  OrganizationBillingPlan.STARTER,
  OrganizationBillingPlan.PROFESSIONAL,
  OrganizationBillingPlan.PRACTICE,
]);

const CHECKOUT_INTERVALS = new Set([
  OrganizationBillingInterval.MONTHLY,
  OrganizationBillingInterval.ANNUAL,
]);

interface SearchParamsReader {
  get: (key: string) => string | null;
}

export interface CheckoutIntent {
  readonly checkoutPlan:
    | typeof OrganizationBillingPlan.STARTER
    | typeof OrganizationBillingPlan.PROFESSIONAL
    | typeof OrganizationBillingPlan.PRACTICE;
  readonly checkoutInterval:
    | typeof OrganizationBillingInterval.MONTHLY
    | typeof OrganizationBillingInterval.ANNUAL;
  readonly checkoutSource: typeof CHECKOUT_SOURCE_PRICING;
}

const isCheckoutPlan = (
  value: string | null | undefined,
): value is CheckoutIntent["checkoutPlan"] => {
  return Boolean(value && CHECKOUT_PLANS.has(value as CheckoutIntent["checkoutPlan"]));
};

const isCheckoutInterval = (
  value: string | null | undefined,
): value is CheckoutIntent["checkoutInterval"] => {
  return Boolean(
    value && CHECKOUT_INTERVALS.has(value as CheckoutIntent["checkoutInterval"]),
  );
};

const parseCheckoutIntent = ({
  checkoutPlan,
  checkoutInterval,
  checkoutSource,
}: {
  checkoutPlan: string | null | undefined;
  checkoutInterval: string | null | undefined;
  checkoutSource: string | null | undefined;
}): CheckoutIntent | null => {
  if (
    !isCheckoutPlan(checkoutPlan) ||
    !isCheckoutInterval(checkoutInterval) ||
    checkoutSource !== CHECKOUT_SOURCE_PRICING
  ) {
    return null;
  }

  return {
    checkoutPlan,
    checkoutInterval,
    checkoutSource,
  };
};

export const getCheckoutIntentFromPricingSelection = ({
  planId,
  interval,
}: {
  planId: string;
  interval: string | null | undefined;
}): CheckoutIntent | null => {
  const checkoutPlan =
    planId === BillingPlan.FREE
      ? OrganizationBillingPlan.STARTER
      : planId === BillingPlan.PREMIUM
        ? OrganizationBillingPlan.PROFESSIONAL
        : planId === BillingPlan.ENTERPRISE
          ? OrganizationBillingPlan.PRACTICE
          : null;

  const checkoutInterval =
    interval === RecurringInterval.MONTH
      ? OrganizationBillingInterval.MONTHLY
      : interval === RecurringInterval.YEAR
        ? OrganizationBillingInterval.ANNUAL
        : null;

  if (!checkoutPlan || !checkoutInterval) {
    return null;
  }

  return {
    checkoutPlan,
    checkoutInterval,
    checkoutSource: CHECKOUT_SOURCE_PRICING,
  };
};

export const getCheckoutIntentFromSearchParams = (
  searchParams: SearchParamsReader | null | undefined,
) => {
  if (!searchParams) {
    return null;
  }

  return parseCheckoutIntent({
    checkoutPlan: searchParams.get(CHECKOUT_PLAN_KEY),
    checkoutInterval: searchParams.get(CHECKOUT_INTERVAL_KEY),
    checkoutSource: searchParams.get(CHECKOUT_SOURCE_KEY),
  });
};

export const appendCheckoutIntentToPath = ({
  path,
  checkoutIntent,
}: {
  path: string;
  checkoutIntent: CheckoutIntent | null;
}) => {
  if (!checkoutIntent) {
    return path;
  }

  const url = new URL(path, "http://localhost");

  url.searchParams.set(CHECKOUT_PLAN_KEY, checkoutIntent.checkoutPlan);
  url.searchParams.set(CHECKOUT_INTERVAL_KEY, checkoutIntent.checkoutInterval);
  url.searchParams.set(CHECKOUT_SOURCE_KEY, checkoutIntent.checkoutSource);

  return `${url.pathname}${url.search}${url.hash}`;
};

export const persistCheckoutIntent = (checkoutIntent: CheckoutIntent) => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    CHECKOUT_INTENT_STORAGE_KEY,
    JSON.stringify(checkoutIntent),
  );
};

export const readStoredCheckoutIntent = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.sessionStorage.getItem(CHECKOUT_INTENT_STORAGE_KEY);

  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<CheckoutIntent>;

    return parseCheckoutIntent({
      checkoutPlan: parsed.checkoutPlan,
      checkoutInterval: parsed.checkoutInterval,
      checkoutSource: parsed.checkoutSource,
    });
  } catch {
    window.sessionStorage.removeItem(CHECKOUT_INTENT_STORAGE_KEY);
    return null;
  }
};

export const clearStoredCheckoutIntent = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.removeItem(CHECKOUT_INTENT_STORAGE_KEY);
};
